import { useCallback, useEffect, useMemo, useState } from 'react'

import { useSelector } from 'react-redux'

import {
  type FlexPortionTrackingContext,
  trackClickEditRecipeQuantity,
  trackSelectPortionCount,
  trackUpdatePortion,
} from '@features/recipe-tile'
import type { StandardRecipeItem } from '@library/api-menu-experience'
import type { RecipeUUID } from '@library/menu-service'

import { useBasket } from 'routes/Menu/domains/basket'
import { getIsAuthenticated } from 'selectors/auth'

import { useMenuExperienceRecipeDetails } from '../menuExperienceRecipeDetailContext'

type RecipeGridFlexData = {
  isEditorOpen: boolean
  isVisible: boolean
  numPortions: number
  portions: StandardRecipeItem['portions']
  quantity: number
}

type FlexRecipeItem = Pick<StandardRecipeItem, 'id' | 'portions'>

type UseRecipeGridFlexOptions<TRecipe extends FlexRecipeItem> = {
  items: TRecipe[]
  removalPosition?: string
}

export const useRecipeGridFlex = <TRecipe extends FlexRecipeItem>({
  items,
  removalPosition,
}: UseRecipeGridFlexOptions<TRecipe>) => {
  const isAuthenticated = useSelector(getIsAuthenticated)
  const { menuId, updateRecipePortions } = useMenuExperienceRecipeDetails()
  const { getNumPortionsForRecipeId, getQuantitiesForRecipeId, recipeCount, removeRecipeByUuid } =
    useBasket()
  const [activeRecipeId, setActiveRecipeId] = useState<RecipeUUID | null>(null)

  const recipeById = useMemo(
    () =>
      Object.fromEntries(items.map((recipe) => [recipe.id, recipe])) as Partial<
        Record<RecipeUUID, TRecipe>
      >,
    [items],
  )
  const recipePositionById = useMemo(
    () =>
      Object.fromEntries(items.map((recipe, index) => [recipe.id, index + 1])) as Partial<
        Record<RecipeUUID, number>
      >,
    [items],
  )

  const getTrackingContext = useCallback(
    (recipeId: RecipeUUID): FlexPortionTrackingContext => {
      return {
        basketRecipeCount: recipeCount,
        menuUuid: menuId,
        recipePortionCount: getNumPortionsForRecipeId(recipeId),
        recipeUuid: recipeId,
        source: 'view_recipe_list_screen',
      }
    },
    [getNumPortionsForRecipeId, menuId, recipeCount],
  )

  const getFlexPortionData = useCallback(
    (recipeId: RecipeUUID): RecipeGridFlexData => {
      const recipe = recipeById[recipeId]
      const quantity = getQuantitiesForRecipeId(recipeId)

      return {
        isEditorOpen: activeRecipeId === recipeId && quantity > 0,
        isVisible: Boolean(isAuthenticated && quantity > 0),
        numPortions: getNumPortionsForRecipeId(recipeId),
        portions: recipe?.portions,
        quantity,
      }
    },
    [
      activeRecipeId,
      getNumPortionsForRecipeId,
      getQuantitiesForRecipeId,
      isAuthenticated,
      recipeById,
    ],
  )

  const openFlexPortionEditor = useCallback(
    (recipeId: RecipeUUID) => {
      const recipe = recipeById[recipeId]

      if (!recipe || !isAuthenticated || getQuantitiesForRecipeId(recipeId) <= 0) {
        return
      }

      if (activeRecipeId !== recipeId) {
        trackClickEditRecipeQuantity(getTrackingContext(recipeId))
      }

      setActiveRecipeId((currentRecipeId) => (currentRecipeId === recipeId ? null : recipeId))
    },
    [activeRecipeId, getQuantitiesForRecipeId, getTrackingContext, isAuthenticated, recipeById],
  )

  const trackFlexPortionEdit = useCallback(
    (recipeId: RecipeUUID) => {
      const recipe = recipeById[recipeId]

      if (!recipe || !isAuthenticated || getQuantitiesForRecipeId(recipeId) <= 0) {
        return
      }

      trackClickEditRecipeQuantity(getTrackingContext(recipeId))
    },
    [getQuantitiesForRecipeId, getTrackingContext, isAuthenticated, recipeById],
  )

  const closeFlexPortionEditor = useCallback(() => setActiveRecipeId(null), [])

  const removeFlexRecipe = useCallback(
    (recipeId: RecipeUUID) => {
      const recipe = recipeById[recipeId]

      if (!recipe) {
        return
      }

      removeRecipeByUuid(
        recipeId,
        'grid',
        removalPosition ?? recipePositionById[recipeId]?.toString() ?? '',
      )

      closeFlexPortionEditor()
    },
    [closeFlexPortionEditor, recipeById, recipePositionById, removalPosition, removeRecipeByUuid],
  )

  const selectFlexPortions = useCallback(
    (recipeId: RecipeUUID, numPortions: number) => {
      trackSelectPortionCount(getTrackingContext(recipeId), numPortions)
    },
    [getTrackingContext],
  )

  const updateFlexPortions = useCallback(
    (recipeId: RecipeUUID, numPortions: number) => {
      updateRecipePortions(recipeId, numPortions)
      trackUpdatePortion(getTrackingContext(recipeId), numPortions)
    },
    [getTrackingContext, updateRecipePortions],
  )

  useEffect(() => {
    if (activeRecipeId) {
      if (getQuantitiesForRecipeId(activeRecipeId) <= 0) {
        setActiveRecipeId(null)
      }
    }
  }, [activeRecipeId, getQuantitiesForRecipeId])

  return {
    closeFlexPortionEditor,
    getFlexPortionData,
    openFlexPortionEditor,
    removeFlexRecipe,
    selectFlexPortions,
    trackFlexPortionEdit,
    updateFlexPortions,
  }
}
